'use client';

import { useMemo, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { ProbeTraceDto } from '@terra/shared-contracts-assets';
import { HeadingSection, MetaDefault } from '../../../components/typography';
import {
  TraceViewToolbar,
  type TraceViewMode,
} from '../../chat/trace-view/TraceViewToolbar';
import { filterMinimizedProbeTraceSteps } from './probe-trace-minimized.utils';
import { filterProbeTraceSteps } from './probe-trace-search.utils';
import { stepHasProbeHttpExchange } from './probe-trace-http.utils';
import { UnverifiedAssetProbeTraceStep } from './UnverifiedAssetProbeTraceStep';

type UnverifiedAssetProbeTraceProps = {
  probeTrace: ProbeTraceDto;
  hideTitle?: boolean;
};

export function UnverifiedAssetProbeTrace({
  probeTrace,
  hideTitle,
}: UnverifiedAssetProbeTraceProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<TraceViewMode>('full');
  const [showHttpOnly, setShowHttpOnly] = useState(false);
  const isMinimized = viewMode === 'minimized';
  const visibleSteps = useMemo(() => {
    const searched = filterProbeTraceSteps({
      steps: isMinimized
        ? filterMinimizedProbeTraceSteps({ steps: probeTrace.steps })
        : probeTrace.steps,
      query: searchQuery,
    });

    if (!showHttpOnly) {
      return searched;
    }

    return searched.filter((step) => stepHasProbeHttpExchange({ step }));
  }, [probeTrace.steps, searchQuery, showHttpOnly, isMinimized]);
  const toolbar = (
    <Box w="fit-content">
      <TraceViewToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showHttpOnly={showHttpOnly}
        onShowHttpOnlyChange={setShowHttpOnly}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
      />
    </Box>
  );

  const contentPx = hideTitle ? '0' : '4';

  return (
    <VStack gap="0" alignItems="stretch" w="full">
      <Box
        borderTopWidth={hideTitle ? '0' : '2px'}
        borderTopColor="border.default"
        borderBottomWidth={hideTitle ? '0' : '1px'}
        borderBottomColor="border.subtle"
        pt={hideTitle ? '0' : '3'}
        pb="3"
        px={contentPx}
      >
        <HStack
          gap="3"
          alignItems="center"
          justifyContent="space-between"
          minW={0}
        >
          {hideTitle ? (
            <Box />
          ) : (
            <HeadingSection color="fg.default" flexShrink={0}>
              {t('probeTrace')}
            </HeadingSection>
          )}
          <Box flex="1" minW={0} display="flex" justifyContent="flex-end">
            {toolbar}
          </Box>
        </HStack>
      </Box>

      <Box px={contentPx} pt="3" pb={hideTitle ? '0' : '3'}>
        {visibleSteps.length === 0 ? (
          <MetaDefault color="fg.muted">{t('probeTraceNoMatches')}</MetaDefault>
        ) : (
          <VStack gap="3" alignItems="stretch" w="full">
            {visibleSteps.map((step) => (
              <UnverifiedAssetProbeTraceStep key={step.id} step={step} />
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
}
